/**
 * Donut chart en SVG con leyenda DEBAJO (full-width, sin truncado).
 * Sin dependencias.
 */
import { useTranslation } from 'react-i18next'
import { ChartTooltip, useChartTooltip } from './ChartTooltip'

interface DataPoint {
  label: string
  value: number
  color?: string
}

interface Props {
  data: DataPoint[]
  size?: number
  /** Formateador de los valores (p.ej. dinero). Por defecto muestra el entero. */
  formatValue?: (n: number) => string
  /** Etiqueta bajo el total central (por defecto "Total"). */
  centerLabel?: string
}

const DEFAULT_COLORS = [
  '#1B4FD8', // azul JR2
  '#10b981', // emerald
  '#f59e0b', // amber
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#84cc16', // lime
  '#f43f5e', // rose
  '#3b82f6', // blue
  '#64748b', // slate
]

export default function DonutChart({ data, size = 180, formatValue, centerLabel }: Props) {
  const { t } = useTranslation('common')
  const { containerRef, tip, show, hide } = useChartTooltip()
  const fmt = formatValue ?? ((n: number) => String(n))
  const total = data.reduce((acc, d) => acc + d.value, 0)
  if (total === 0) {
    return <div className="text-xs t-faint text-center py-8">{t('donutChart.noData')}</div>
  }

  const R = size / 2 - 6
  const r = R * 0.6
  const cx = size / 2
  const cy = size / 2

  let cumulative = 0
  const segments = data.map((d, i) => {
    const start = cumulative
    cumulative += d.value
    const end = cumulative
    const startA = (start / total) * Math.PI * 2 - Math.PI / 2
    const endA = (end / total) * Math.PI * 2 - Math.PI / 2
    const largeArc = end - start > total / 2 ? 1 : 0
    const x1 = cx + R * Math.cos(startA)
    const y1 = cy + R * Math.sin(startA)
    const x2 = cx + R * Math.cos(endA)
    const y2 = cy + R * Math.sin(endA)
    const x3 = cx + r * Math.cos(endA)
    const y3 = cy + r * Math.sin(endA)
    const x4 = cx + r * Math.cos(startA)
    const y4 = cy + r * Math.sin(startA)
    const path = [
      `M ${x1} ${y1}`,
      `A ${R} ${R} 0 ${largeArc} 1 ${x2} ${y2}`,
      `L ${x3} ${y3}`,
      `A ${r} ${r} 0 ${largeArc} 0 ${x4} ${y4}`,
      'Z',
    ].join(' ')
    return {
      path,
      color: d.color ?? DEFAULT_COLORS[i % DEFAULT_COLORS.length]!,
      label: d.label,
      value: d.value,
      percent: (d.value / total) * 100,
    }
  })

  // Leyenda en 1 columna si ≤ 4 entradas, en 2 columnas si son más.
  // Así se lee bien sin truncar y el alto se mantiene razonable.
  const legendCols = segments.length <= 4 ? 'grid-cols-1' : 'grid-cols-2'

  const tipFor = (s: (typeof segments)[number]) => ({
    title: s.label,
    rows: [{ value: `${fmt(s.value)} · ${s.percent.toFixed(1)}%`, color: s.color }],
  })

  return (
    <div ref={containerRef} className="relative flex flex-col items-center gap-3" onMouseLeave={hide}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {segments.map((s, i) =>
          // Un segmento que cubre el círculo completo (100%) no puede dibujarse
          // con un arco (inicio == fin, path degenerado): lo pintamos como anillo.
          s.percent >= 99.9999 ? (
            <circle
              key={i}
              cx={cx}
              cy={cy}
              r={(R + r) / 2}
              fill="none"
              stroke={s.color}
              strokeWidth={R - r}
              onMouseMove={(e) => show(e, tipFor(s))}
            >
              <title>
                {s.label}: {fmt(s.value)} ({s.percent.toFixed(1)}%)
              </title>
            </circle>
          ) : (
            <path key={i} d={s.path} fill={s.color} onMouseMove={(e) => show(e, tipFor(s))}>
              <title>
                {s.label}: {fmt(s.value)} ({s.percent.toFixed(1)}%)
              </title>
            </path>
          )
        )}
        <text
          x={cx}
          y={cy - 4}
          textAnchor="middle"
          className={`fill-current font-semibold ${formatValue ? 'text-[12px]' : 'text-[18px]'}`}
        >
          {fmt(total)}
        </text>
        <text
          x={cx}
          y={cy + 12}
          textAnchor="middle"
          className="fill-current text-[9px] opacity-60"
        >
          {centerLabel ?? t('donutChart.total')}
        </text>
      </svg>

      <ul className={`text-xs w-full grid ${legendCols} gap-x-3 gap-y-1`}>
        {segments.map((s, i) => (
          <li
            key={i}
            className="flex items-center gap-2"
            onMouseMove={(e) => show(e, tipFor(s))}
            onMouseLeave={hide}
          >
            <span
              className="w-2.5 h-2.5 rounded-sm shrink-0"
              style={{ background: s.color }}
            />
            <span className="t-secondary flex-1">{s.label}</span>
            <span className="t-faint font-mono">{fmt(s.value)}</span>
            <span className="t-faint text-[10px] w-9 text-right">
              {s.percent.toFixed(0)}%
            </span>
          </li>
        ))}
      </ul>
      <ChartTooltip tip={tip} />
    </div>
  )
}
